import React, { useEffect, useState } from 'react';
import { ClientApiProps, parseQuery } from '../../common';
import { AlertProvider } from 'acma-spa/dist/layout/Alert';
import { useLocation, useNavigate } from 'react-router-dom';
import { Client, getApiClient } from '../../api';
import { Button } from 'acma-spa';
import { faCropSimple } from '@fortawesome/free-solid-svg-icons';
import { Link } from '../../common/FormHelpers';

export interface CallsignTransferPageProps extends ClientApiProps {
  onSpinner: (msg: string) => any;
}

export const CallsignTransferPage: React.FC<CallsignTransferPageProps> = (props) => {
  const location = useLocation();
  const query = parseQuery(location.search);
  const containerId = 'callsign-transfer';
  const [model, setModel] = useState('');
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const load = async (client: Client) => {
    props.onSpinner('Loading...');
    try {
      const res = await client.transfer(query.invitationId);
      if (typeof res.error === 'undefined') {
        setMessage('');
        setIsError(false);
      } else {
        setMessage(res.error ?? 'Unexpected error.');
        setIsError(true);
      }
    } catch (e: any) {
      setMessage(e.message ? e.message : 'Unexpected error.');
      setIsError(true);
    } finally {
      props.onSpinner('');
    }
  };

  useEffect(() => {
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    load(client);
    return function () {
      abort.abort();
    };
  }, []);

  const onAccept = async () => {
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

    props.onSpinner('Saving...');
    try {
      const res = await client.accept(query.invitationId);
      if (typeof res.error === 'undefined') {
        setMessage('Saved successfully');
        setIsError(false);
      } else {
        if (res.error === 'Failed to process payment.') {
          setMessage('The request has expired and an email notification will be sent to the amateur operator who made the request.');
          setIsError(true);
        } else {
          setMessage(res.error ?? 'Unexpected error.');
          setIsError(true);
        }
      }
    } catch (e: any) {
      setMessage(e.message ? e.message : 'Unexpected error.');
      setIsError(true);
    } finally {
      props.onSpinner('');
    }
    window.scroll(0, 0);
  };

  const onReject = async () => {
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    props.onSpinner('Saving...');
    try {
      const res = await client.reject(query.invitationId);
      if (typeof res.error === 'undefined') {
        setMessage('Saved successfully');
        setIsError(false);
      } else {
        setMessage(res.error ?? 'Unexpected error.');
        setIsError(true);
      }
    } catch (e: any) {
      setMessage(e.message ? e.message : 'Unexpected error.');
      setIsError(true);
    } finally {
      props.onSpinner('');
    }
    window.scroll(0, 0);
  };

  const PrivacyPolicy = () => <>
    <div className="alert alert-info" role="alert">
      The personal information being collected is to enable the ACMA to consider whether transfer of this call sign to you is consistent with the {Link(props.contentUrls.callSignPolicy, 'ACMA Call sign policy')}. The information will also be used by the ACMA to administer call signs and facilitate call sign applications and assignments.
      <br />
      <br />
      The ACMA publishes a call sign register on its website that lists all call signs and, if the call sign is allocated, the respective qualification level associated with the allocated call sign. The call sign register does not contain any other information about a licence holder.
      <br />
      <br />
      The ACMA will not use any of the personal information for any other purpose, nor will we disclose it, unless we have your consent or we are otherwise permitted to do so under the <em>Privacy Act 1988</em>.
      <br />
      <br />
      Further information about the {Link(props.contentUrls.privacyPolicy, "Privacy Act and the ACMA's Privacy Policy")} is on our website. The Privacy Policy contains details about how you may access personal information about you that is held by the ACMA, and seek the correction of such information. It also explains how you may complain about a breach of the {Link(props.contentUrls.privacyAct, 'Privacy Act')} and how we will deal with such a complaint.
    </div>
  </>

  return (
    <>
      <div id={containerId}>
        {(message && <AlertProvider messages={[message]} color={isError ? 'danger' : 'info'} />) || (
          <>
            <PrivacyPolicy />

            <p>Select <b>Yes</b> if you want to accept the transfer of the call sign or <b>No</b> if you want to decline the transfer.</p>

            <div className="col-xs-6 float-left">
              <Button label="Yes" id="accept" onClick={onAccept} />
            </div>
            <div className="col-xs-6 float-right">
              <Button label="No" id="reject" onClick={onReject} />
            </div>
          </>
        )}
      </div>
    </>
  );
};
